import { createContext, useContext } from "react";

import type { PreviewableDoc } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";

/** What the signature panel was opened on. */
export interface SignaturePanelTarget {
  containerId: string;
  /** Set when opened from a List/Timeline row — the document inside the container. */
  enclosedDocumentId?: string | null;
  /** Filename of that enclosed document, for the header and download action. */
  enclosedFilename?: string | null;
}

/** The container the panel's signing action should add a signature to. */
export interface SignableContainer {
  id: string;
  filename: string;
  size: number | null;
}

/**
 * The panel's footer actions are supplied by the view that mounts the provider,
 * which owns the share and signing dialogs. Missing handlers simply drop those
 * buttons (for example, the read-only share page has neither).
 */
export interface SignaturePanelActions {
  onShare?: (containerId: string) => void;
  onSign?: (container: SignableContainer) => void;
}

export interface SignaturePanelApi {
  open: (target: SignaturePanelTarget) => void;
}

export const SignaturePanelContext = createContext<SignaturePanelApi | null>(null);

/**
 * The view's signature panel, or `null` when no provider is mounted — callers
 * then render the signed indicators without a click affordance.
 */
export function useSignaturePanel(): SignaturePanelApi | null {
  return useContext(SignaturePanelContext);
}

/**
 * The document viewer the panel provider owns, for a view that opens its OWN
 * rows in it too (the Signing page). One viewer per page means one preview
 * state, one held blob and one error toast — a second `useDocumentPreview`
 * beside it would hold its own blob and toast the same failure again.
 */
export interface SignaturePanelViewerApi {
  openPreviewByDoc: (doc: PreviewableDoc) => Promise<void>;
  /** The id of the document downloading for the viewer right now (it opens
   *  once the blob is in), or null. Whoever started the load — the panel or
   *  the view — this names the document actually loading, so a control
   *  can spin and guard re-clicks for its own file only. */
  loadingDocumentId: string | null;
}

export const SignaturePanelViewerContext = createContext<SignaturePanelViewerApi | null>(null);

/** The panel provider's viewer, or `null` outside one. */
export function useSignaturePanelViewer(): SignaturePanelViewerApi | null {
  return useContext(SignaturePanelViewerContext);
}
